import { useCallback, useState } from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { H3 } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderLayout,
} from '@amzn/stencil-react-components/expander';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';

import { RichTextEditorField } from '../../../shared/ui/RichTextEditorField';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import {
  TaskDetailsFields,
  type TaskDetailsValue,
} from '../../../shared/ui/forms/TaskDetailsFields';
import { Divider } from '../../../shared/ui/Divider';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SubQuestionRequirementToggle } from './SubQuestionRequirementToggle';
import { SubQuestionDataCollectionFields } from './SubQuestionDataCollectionFields';
import { SubQuestionResponseTypePicker } from './SubQuestionResponseTypePicker';
import { SubQuestionRowHeader } from './SubQuestionRowHeader';
import { responseTypeLabelKey } from './responseTypeOptions';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';
import { isDataCollectionType } from '../../../shared/domain/responseTypeClassification';
import {
  DEFAULT_RESPONSE_TYPE,
  normalizeSubForResponseType,
} from '../../../shared/domain/subQuestionResponseType';
import { ShowOn, type ResponseType } from '../../../../types/helios/Question';
import type { SubQuestion, TaskDetails } from '../logic/reviewTypes';

/** An empty task shell used when a sub-question gains a task. Module-level for
 *  reference stability. */
const EMPTY_TASK_DETAILS: TaskDetails = { taskName: '', taskDescription: '' };

/** Shared `justifyContent` value for the task-toggle row. */
const SPACE_BETWEEN = 'space-between';

/** Returns the sub-question without its `taskDetails` key (rather than setting
 *  it undefined) so it stays clean under exactOptionalPropertyTypes. */
function stripTask(sub: SubQuestion): SubQuestion {
  const entries = Object.entries(sub).filter(([key]) => key !== 'taskDetails');
  return Object.fromEntries(entries) as unknown as SubQuestion;
}

/** Normalize a TaskDetailsValue to the Helios TaskDetails shape (omit the
 *  duration key when absent). */
function toTaskDetails(next: TaskDetailsValue): TaskDetails {
  if (next.taskDurationDays === undefined) {
    return { taskName: next.taskName, taskDescription: next.taskDescription };
  }
  return {
    taskName: next.taskName,
    taskDescription: next.taskDescription,
    taskDurationDays: next.taskDurationDays,
  };
}

interface RowToggleConfig {
  readonly key: string;
  readonly label: string;
  readonly info: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly field: string;
}

export interface SubQuestionRowProps {
  readonly sub: SubQuestion;
  readonly index: number;
  readonly disabled: boolean;
  readonly onChange: (index: number, next: SubQuestion) => void;
  readonly onDelete: (index: number) => void;
  /** Whether the row starts expanded. Existing rows start collapsed (matches
   *  the mock); a just-added row is passed `true` so the author can fill it in. */
  readonly defaultExpanded?: boolean;
  /** Whether the parent question has conditional sub-questions enabled. When
   *  true, a data-collection sub shows the "Show when parent is" selector.
   *  (A Pass/Fail sub can never be conditional — the parent gate blocks
   *  enabling conditional while any P/F sub exists.) */
  readonly conditionalEnabled?: boolean;
  /** When true, this row's response-type badge is shown in a warning color to
   *  flag it as blocking conditional display (a Pass/Fail sub while the parent
   *  tried to enable conditional display). Cleared when the block resolves. */
  readonly highlightAsInvalid?: boolean;
  readonly dataTestId?: string;
}

/**
 * One collapsible sub-question editor row: title, rich-text instruction, the
 * four Pass/Fail requirement toggles, and an optional task (add/remove) via
 * the shared `TaskDetailsFields`. Controlled — patches the sub-question and
 * hands it up via `onChange(index, next)`.
 */
export function SubQuestionRow({
  sub,
  index,
  disabled,
  onChange,
  onDelete,
  defaultExpanded = false,
  conditionalEnabled = false,
  highlightAsInvalid = false,
  dataTestId,
}: SubQuestionRowProps): JSX.Element {
  const { t, f, isLoading: isBundleLoading } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);
  // Existing rows start collapsed (matches the mock); a just-added row is
  // seeded expanded via defaultExpanded. The header chevron toggles it after.
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const patch = useCallback(
    (fields: Partial<SubQuestion>) => onChange(index, { ...sub, ...fields }),
    [index, sub, onChange],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value }),
    [patch],
  );
  const handleInstructionChange = useCallback(
    (html: string) => patch({ instruction: html }),
    [patch],
  );
  const handleCommentOnFail = useCallback(
    (checked: boolean) => patch({ requireCommentOnFail: checked }),
    [patch],
  );
  const handleEvidenceOnFail = useCallback(
    (checked: boolean) => patch({ requireEvidenceOnFail: checked }),
    [patch],
  );
  const handleCommentOnPass = useCallback(
    (checked: boolean) => patch({ requireCommentOnPass: checked }),
    [patch],
  );
  const handleEvidenceOnPass = useCallback(
    (checked: boolean) => patch({ requireEvidenceOnPass: checked }),
    [patch],
  );

  // Response type change: rebuild the sub via the pure normalizer (drops
  // fields that no longer apply — scored P/F flags ↔ unconditional flags,
  // options, conditional trigger) and emit the clean result. No inline
  // mutation; the whole next sub is handed up through onChange.
  const handleResponseTypeChange = useCallback(
    (value: string) =>
      onChange(index, normalizeSubForResponseType(sub, value as ResponseType)),
    [index, sub, onChange],
  );
  const handleShowOnChange = useCallback(
    (value: string) => patch({ showWhenParentIs: value as ShowOn }),
    [patch],
  );

  const responseType = sub.responseType ?? DEFAULT_RESPONSE_TYPE;
  const isDataCollection = isDataCollectionType(sub.responseType);

  const handleTaskChange = useCallback(
    (next: TaskDetailsValue) => patch({ taskDetails: toTaskDetails(next) }),
    [patch],
  );
  const handleAddTask = useCallback(
    () => patch({ taskDetails: EMPTY_TASK_DETAILS }),
    [patch],
  );
  const handleRemoveTask = useCallback(
    () => onChange(index, stripTask(sub)),
    [index, sub, onChange],
  );
  // In the header, stop propagation so the delete click doesn't also toggle
  // the surrounding expander trigger.
  const handleDeleteFromHeader = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete(index);
    },
    [index, onDelete],
  );

  const headerText = f('subRowLabel', {
    number: index + 1,
    title: sub.title.trim().length > 0 ? sub.title : t('subUntitledLabel'),
  });

  const hasTask = sub.taskDetails !== undefined;
  const taskValue: TaskDetailsValue = sub.taskDetails ?? EMPTY_TASK_DETAILS;

  const toggles: readonly RowToggleConfig[] = [
    {
      key: 'commentOnFail',
      label: t('subCommentOnFailLabel'),
      info: t('subCommentOnFailInfo'),
      checked: sub.requireCommentOnFail ?? false,
      onChange: handleCommentOnFail,
      field: 'require-comment-on-fail',
    },
    {
      key: 'evidenceOnFail',
      label: t('subEvidenceOnFailLabel'),
      info: t('subEvidenceOnFailInfo'),
      checked: sub.requireEvidenceOnFail ?? false,
      onChange: handleEvidenceOnFail,
      field: 'require-evidence-on-fail',
    },
    {
      key: 'commentOnPass',
      label: t('subCommentOnPassLabel'),
      info: t('subCommentOnPassInfo'),
      checked: sub.requireCommentOnPass ?? false,
      onChange: handleCommentOnPass,
      field: 'require-comment-on-pass',
    },
    {
      key: 'evidenceOnPass',
      label: t('subEvidenceOnPassLabel'),
      info: t('subEvidenceOnPassInfo'),
      checked: sub.requireEvidenceOnPass ?? false,
      onChange: handleEvidenceOnPass,
      field: 'require-evidence-on-pass',
    },
  ];
  // Two rows of two (Fail pair, Pass pair) for the 2×2 grid.
  const toggleRows: readonly (readonly RowToggleConfig[])[] = [
    toggles.slice(0, 2),
    toggles.slice(2, 4),
  ];
  const infoAltText = t('subInfoAltText');

  // Render the row header as: [title text] … [delete button], so delete sits
  // inline on the header instead of on its own line. The delete button stops
  // propagation so clicking it doesn't also toggle the expander.
  // Response-type badge label on the collapsed header so the author can scan
  // each sub's type without expanding.
  const typeBadgeLabel = t(responseTypeLabelKey(sub.responseType));

  const renderTitle = useCallback(
    ({ titleText, toggle }: { titleText?: string; toggle: () => void }): JSX.Element => (
      <SubQuestionRowHeader
        titleText={titleText}
        onToggle={toggle}
        typeBadgeLabel={typeBadgeLabel}
        highlightAsInvalid={highlightAsInvalid}
        onDelete={handleDeleteFromHeader}
        deleteAltText={t('subDeleteAltText')}
        disabled={disabled}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
    ),
    [t, handleDeleteFromHeader, disabled, dataTestId, typeBadgeLabel, highlightAsInvalid],
  );

  return (
    <Expander
      titleText={headerText}
      layout={ExpanderLayout.Card}
      chevronPosition={ExpanderChevronPosition.Leading}
      isExpanded={isExpanded}
      onToggle={setIsExpanded}
      renderTitle={renderTitle}
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.300" width="100%">
        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={false}>
            {t('subTitleLabel')}
          </RequiredLabel>
          <Input
            value={sub.title}
            onChange={handleTitleChange}
            placeholder={t('subTitlePlaceholder')}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-title` : undefined}
          />
        </Col>

        <Col gridGap="dimensions.spacing.100">
          <RequiredLabel isRequired hasError={false}>
            {t('subInstructionLabel')}
          </RequiredLabel>
          {/* Stencil's RichTextEditor bakes its placeholder into the TipTap
           *  Placeholder extension at editor-creation and never refreshes it
           *  (useEditor has no deps — see rte/RichTextEditor.js "TODO: Add
           *  dependencies"). Hold the editor until the i18n bundle resolves so
           *  it isn't created with the raw key as its placeholder. */}
          {isBundleLoading ? null : (
            <RichTextEditorField
              value={sub.instruction}
              onChange={handleInstructionChange}
              placeholder={t('subInstructionPlaceholder')}
              disabled={disabled}
              ariaLabel={t('subInstructionLabel')}
              {...(dataTestId !== undefined
                ? { dataTestId: `${dataTestId}-instruction` }
                : {})}
            />
          )}
        </Col>

        {/* Response type (+ conditional trigger). Defaults to Pass/Fail
         *  (agent seed); changing it rebuilds the sub via the normalizer so
         *  stale per-type fields never ride along. */}
        <SubQuestionResponseTypePicker
          responseType={responseType}
          onResponseTypeChange={handleResponseTypeChange}
          conditionalEnabled={conditionalEnabled}
          excludePassFail={conditionalEnabled}
          {...(sub.showWhenParentIs !== undefined
            ? { showWhenParentIs: sub.showWhenParentIs }
            : {})}
          onShowOnChange={handleShowOnChange}
          disabled={disabled}
          {...(dataTestId !== undefined ? { dataTestId } : {})}
        />

        {/* Answer-config body: data-collection subs get the options editor +
         *  unconditional requirement toggles; scored P/F subs keep the four
         *  on-fail / on-pass toggles. */}
        {isDataCollection ? (
          <SubQuestionDataCollectionFields
            sub={sub}
            responseType={responseType}
            disabled={disabled}
            patch={patch}
            {...(dataTestId !== undefined ? { dataTestId } : {})}
          />
        ) : (
          // 2×2 grid (Comment | Attachment on Fail, then on Pass), matching the
          // Question Configuration step's RequirementTogglesGrid.
          toggleRows.map((toggleRow) => (
            <Row
              key={toggleRow.map((toggle) => toggle.key).join('-')}
              gridGap="dimensions.spacing.400"
              flexWrap="wrap"
            >
              {toggleRow.map((toggle) => (
                <View key={toggle.key} flex={1} minWidth={0}>
                  <SubQuestionRequirementToggle
                    label={toggle.label}
                    info={toggle.info}
                    infoAltText={infoAltText}
                    checked={toggle.checked}
                    onChange={toggle.onChange}
                    disabled={disabled}
                    trackingId={`audit-whiz.review.sub-q.${sub.orderIndex}.${toggle.field}`}
                    {...(dataTestId !== undefined
                      ? { dataTestId: `${dataTestId}-${toggle.key}` }
                      : {})}
                  />
                </View>
              ))}
            </Row>
          ))
        )}

        <Divider />

        <Row alignItems="center" justifyContent={SPACE_BETWEEN} flexWrap="wrap">
          <H3>{t('subTaskHeading')}</H3>
          <Button
            variant={ButtonVariant.Tertiary}
            size={ButtonSize.Small}
            onClick={hasTask ? handleRemoveTask : handleAddTask}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-task-toggle` : undefined}
          >
            {hasTask ? t('subRemoveTaskButton') : t('subAddTaskButton')}
          </Button>
        </Row>

        {hasTask && !isBundleLoading && (
          <TaskDetailsFields
            value={taskValue}
            onChange={handleTaskChange}
            disabled={disabled}
            labels={{
              nameLabel: t('criteriaTaskNameLabel'),
              namePlaceholder: t('criteriaTaskNamePlaceholder'),
              descriptionLabel: t('criteriaTaskDescriptionLabel'),
              descriptionPlaceholder: t('criteriaTaskDescriptionPlaceholder'),
              durationLabel: t('criteriaTaskDurationLabel'),
              durationPlaceholder: t('criteriaTaskDurationPlaceholder'),
            }}
            {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-task` } : {})}
          />
        )}
      </Col>
    </Expander>
  );
}
